import { useCallback } from 'react'
import {
  AssinaturaType,
  ControlError,
  ModalSize,
  showAlertLoading,
  showFillCertificado,
} from '../../../_metronic/helpers'
import { ClearanceDto, TokenDtoSignature, TokenDtoValidate, useAuth } from '../../modules/auth'
import { validateToken, refreshTokenForSignature } from '../../modules/auth/core/_requests'
import { loadTipoCert, saveTipoCert, clearTipoCert } from '../../modules/auth/core/AuthHelpers'
import { useModal } from '../../components'
import { CertificadoModal } from '../modal/CertificadoModal'
import Swal from 'sweetalert2'
import { ClearancesModal } from '../modal/ClearancesModal'
import { checkCredential, getClearances } from '../../modules/public/certificado/core/_request'
import { useT } from '_metronic/i18n/Metronici18n'

export type AssinaturaResult = {
  ok: boolean
  tipo?: AssinaturaType
}

export const useCertificado = () => {
  const { showModal } = useModal()
  const { currentUser, auth, saveAuth } = useAuth()
  const { t } = useT()

  const selectTipoModal = useCallback(() => {
    return new Promise<AssinaturaType | null>((resolve, reject) => {
      showModal({
        title: 'Selecione o tipo de assinatura',
        size: ModalSize.Medium,
        content: <CertificadoModal onSubmit={resolve} />,
        onClose: () => reject(new Error(ControlError.ModalCancelled)),
      })
    })
  }, [showModal])

  const selectClearanceModal = useCallback(
    (clearances: ClearanceDto[]) => {
      return new Promise<void>((resolve, reject) => {
        showModal({
          title: 'Selecione o certificado digital',
          size: ModalSize.Large,
          content: <ClearancesModal clearances={clearances} onSubmit={resolve} />,
          onClose: () => reject(new Error(ControlError.ModalCancelled)),
        })
      })
    },
    [showModal]
  )

  const ensurePasswordA1 = async (): Promise<boolean> => {
    if (!auth) return false

    const token: TokenDtoValidate = { token: auth.accessToken }

    const { assinatura, valido } = await validateToken(token)

    if (valido && assinatura) return true

    const senha = await showFillCertificado(t)
    if (!senha) {
      clearTipoCert()
      return false
    }

    const tokenSignature: TokenDtoSignature = {
      senha,
      token: auth.accessToken,
    }
    try {
      const newAuth = await refreshTokenForSignature(tokenSignature)
      showAlertLoading()
      saveAuth(newAuth)
      return true
    } catch (error) {
      clearTipoCert()
      throw error
    }
  }

  const checkCredentialA3 = async () => {
    return await checkCredential()
  }

  const getClearancesA3 = async () => {
    try {
      const data = await getClearances()
      return data
    } catch (error) {
      throw error
    }
  }

  const changeTipoCertificado = async (): Promise<AssinaturaType | null> => {
    const possuiCertificadoDigital = currentUser?.pessoa?.certificadoDigital

    if (!possuiCertificadoDigital) {
      return null
    }

    const tipo = await selectTipoModal()

    if (!tipo) return null

    saveTipoCert(tipo)

    return tipo
  }

  const ensurePassword = async (): Promise<AssinaturaResult> => {
    let tipo = loadTipoCert()

    const possuiCertificadoDigital = currentUser?.pessoa?.certificadoDigital

    if (!possuiCertificadoDigital) {
      tipo = AssinaturaType.A3_Nuvem
      saveTipoCert(tipo)
    }

    try {
      if (possuiCertificadoDigital && !tipo) {
        Swal.close()
        tipo = await selectTipoModal()

        if (!tipo) return { ok: false }
      }
      if (tipo === AssinaturaType.A1) {
        const senhaOk = await ensurePasswordA1()
        return {
          ok: senhaOk,
          tipo,
        }
      }

      if (tipo === AssinaturaType.A3_Nuvem) {
        showAlertLoading()
        const credValida = await checkCredentialA3()
        if (credValida) {
          return { ok: true, tipo }
        }

        try {
          const clearances = await getClearancesA3()
          if (!clearances || !clearances.length) return { ok: false }
          await selectClearanceModal(clearances)
          showAlertLoading()
        } catch (error) {
          clearTipoCert()
          throw error
        }

        return { ok: true, tipo }
      }
      return { ok: false }
    } catch (error) {
      if (error instanceof Error && error.message === ControlError.ModalCancelled) {
        return { ok: false }
      }
      throw error
    }
  }

  return {
    ensurePassword,
    changeTipoCertificado,
  }
}
